import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllGroups } from "@/services/firebase/group-functions";
import { GroupModel } from "common247";
import { GroupCard } from "@/components/groups/GroupCard";
import { entityCache } from "@/services/sync/entity-cache";

const GROUP_SORT_OPTIONS: ISortOption<GroupModel>[] = [
  { key: "name", label: "Group Name", getValue: (g) => g.name || "" },
  { key: "owner", label: "Owner", getValue: (g) => g.ownerDisplayName || "" },
  { key: "memberCount", label: "Members Count", getValue: (g) => g.contactIds?.length || 0 },
  { key: "createdAt", label: "Created Date", getValue: (g) => g.createdAt || 0 },
];

const GROUP_GROUP_OPTIONS: IGroupOption<GroupModel>[] = [
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (g) => g.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "type",
    label: "Group Type",
    getGroupValue: (g) => (g.groupType === "allcontacts" ? "System All-Contacts Groups" : "Custom User Groups"),
  },
];

/**
 * Administrative screen displaying all user groups with view, sort, group, and filter capabilities.
 */
export default function GroupAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchGroups = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllGroups(services, {});
      if (response.status) {
        setGroups(response.groups || []);
        if (response.groups && response.groups.length > 0) {
          entityCache.setMany("group", response.groups);
        }
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load groups.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching groups.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchGroups();
    }, [fetchGroups]),
  );

  return (
    <ThemedScreen title="Group Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${groups.length} TOTAL GROUPS`}
        </ThemedText>

        <ThemedList<GroupModel>
          data={groups}
          sortOptions={GROUP_SORT_OPTIONS}
          defaultSortKey="name"
          defaultSortDirection="asc"
          groupOptions={GROUP_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-groups"
          pageSize={24}
          filterKeys={["name", "description", "ownerDisplayName"]}
          filterPlaceholder="Search all groups..."
          loading={loading}
          onRefresh={() => fetchGroups(true)}
          refreshing={refreshing}
          emptyText="No groups found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <GroupCard
              group={item}
              allGroups={groups}
              isAdminView={true}
              canEditTags={false}
              viewMode={viewMode}
              onInspect={() => CustomAlert.alert("Info", "Group inspection coming soon.")}
            />
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
  },
});
